import { animate, AnimationEvent, state, style, transition, trigger } from '@angular/animations';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  inject,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  BehaviorSubject,
  distinctUntilChanged,
  filter,
  first,
  firstValueFrom,
  map,
  Observable,
  startWith,
} from 'rxjs';

import { runtimeActions, runtimeSelectors } from '@blaze-runtime/engine-data-access';
import {
  type BorderRadiusValue,
  DEFAULT_ELEMENT_PADDING,
  getActiveBinding,
  getBackground,
  getBorderColor,
  makeBorderStyles,
  makeContainerVisualEffectStyles,
} from '@blaze-shared/elements-domain';
import { LocalStorageService, StorageKey } from '@blaze-shared/platform-data-access';

import { CanvasElementComponent } from '../../canvas-elements/element-component';
import { CanvasElementBaseComponent } from '../__base/base.component';

import { INITIAL_SLOT_ID } from './collapsible-container.models';

@Component({
  selector: 'be-collapsible-container',
  animations: [
    trigger('collapsed', [
      state(
        'true',
        style({
          height: 0,
          paddingTop: 0,
          paddingBottom: 0,
        }),
      ),
      state(
        'false',
        style({
          height: '*',
          paddingTop: '*',
          paddingBottom: '*',
        }),
      ),
      transition('* => *', [animate('0.5s ease')]),
    ]),
  ],
  templateUrl: './collapsible-container.component.html',
  styleUrls: ['./collapsible-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CollapsibleContainerComponent
  extends CanvasElementBaseComponent<'collapsibleContainer'>
  implements CanvasElementComponent<'collapsibleContainer'>, OnChanges, OnInit
{
  private readonly localStorageService = inject(LocalStorageService);
  @ViewChild('content') content?: ElementRef<HTMLElement>;

  @HostBinding('style.--b-cc-internal-padding') internalPadding = DEFAULT_ELEMENT_PADDING;
  @HostBinding('style.--b-cc-container-border-radius') containerBorderRadius: BorderRadiusValue =
    '0px';
  @HostBinding('style.--b-cc-header-title-color') headerTitleColor = '#000';
  @HostBinding('style.--b-cc-header-icon-color') headerIconColor = '#000';
  @HostBinding('style.--b-cc-header-fill') headerFill = '#fff';
  @HostBinding('style.--b-cc-content-divider-color') contentDividerColor = '#eee';
  @HostBinding('style.--b-cc-content-fill') contentFill = '#fff';

  private initialState = true;
  private projectId!: string;

  protected readonly slotId = INITIAL_SLOT_ID;

  hasContent$: Observable<boolean>;
  isAnimating$$ = new BehaviorSubject<boolean>(false);

  collapsed?: boolean;
  fullyOpen?: boolean;
  fullyClosed?: boolean;

  title = '';
  showChevron = true;
  chevron = '';
  wrapperStyles: Record<string, string> | null = null;

  set isAnimating(isAnimating: boolean) {
    this.isAnimating$$.next(isAnimating);
  }

  get isAnimating(): boolean {
    return this.isAnimating$$.value;
  }

  constructor() {
    super();
    this.initMarginHandler((props) => props?.style?.general?.offset?.margin);

    this.hasContent$ = this.element$.pipe(
      map((element) => element.slots[element.slots.ids[this.slotId]]?.children.length === 0),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.props) {
      this.title = this.props?.content.header?.title ?? this.title;
      this.showChevron = this.props?.content.header?.showChevron ?? this.showChevron;
      this.chevron = this.props?.content.header?.chevron ?? this.chevron;
      this.updateStyles();
    }
  }

  ngOnInit(): void {
    this.store
      .select(runtimeSelectors.selectProjectId)
      .pipe(first())
      .subscribe((projectId) => {
        this.projectId = projectId;
      });

    this.dataInput$
      .pipe(
        map((d) => d?.opened?.data),
        startWith(getActiveBinding(this.props?.data, 'opened') ? undefined : true),
        filter((isOpen) => isOpen !== undefined),
        distinctUntilChanged(),
        map((isOpen) => {
          if (this.initialState) {
            this.initialState = false;
            const state =
              this.localStorageService.getForProject<Record<string, boolean>>(
                this.projectId,
                StorageKey.ElementCollapsibleContainerOpen,
              ) ?? {};
            if (state[this.elementId] !== undefined) {
              return state[this.elementId];
            }
          }
          return isOpen;
        }),
      )
      .subscribe((isOpen) => {
        if (this.collapsed === undefined) {
          this.collapsed = !isOpen;
          this.fullyOpen = isOpen;
          this.fullyClosed = !isOpen;
          if (!isOpen) {
            this.store.dispatch(
              runtimeActions.updateElementMeta({
                elementId: this.elementId,
                meta: {
                  renderFeatures: {
                    autoHeight: true,
                  },
                },
              }),
            );
          }
        } else if (this.collapsed === isOpen) {
          this.toggle();
        }
      });
  }

  async showSlot(): Promise<void> {
    await firstValueFrom(this.isAnimating$$.pipe(filter((animating) => !animating)));
    if (!this.collapsed) {
      return;
    }
    this.toggle();
  }

  toggle(): void {
    if (this.isAnimating) {
      return; // Prevent multiple clicks
    }

    if (!this.collapsed) {
      this.store.dispatch(
        runtimeActions.updateElementMeta({
          elementId: this.elementId,
          meta: {
            renderFeatures: {
              autoHeight: true,
            },
          },
        }),
      );
      this.fullyOpen = false;
    } else {
      this.fullyClosed = false;
    }
    this.collapsed = !this.collapsed;
    this.cdr.detectChanges();

    const state =
      this.localStorageService.getForProject<Record<string, boolean>>(
        this.projectId,
        StorageKey.ElementCollapsibleContainerOpen,
      ) ?? {};
    const newState = { ...state, [this.elementId]: !this.collapsed };
    this.localStorageService.setForProject(
      this.projectId,
      StorageKey.ElementCollapsibleContainerOpen,
      newState,
    );
  }

  collapseStart(_event: AnimationEvent): void {
    this.isAnimating = true;
  }

  collapseDone(event: AnimationEvent): void {
    this.isAnimating = false;

    if (event.fromState === 'void') {
      setTimeout(() => {
        this.content?.nativeElement.style.removeProperty('height');
      });
    }

    if (String(event.fromState) === 'true' && String(event.toState) === 'false') {
      setTimeout(() => {
        this.store.dispatch(
          runtimeActions.updateElementMeta({
            elementId: this.elementId,
            meta: {
              renderFeatures: {
                autoHeight: false,
              },
            },
          }),
        );
        this.content?.nativeElement.style.removeProperty('height');
      });
      this.fullyOpen = true;
      this.fullyClosed = false;
    }

    if (String(event.fromState) === 'false' && String(event.toState) === 'true') {
      this.store.dispatch(
        runtimeActions.updateElementMeta({
          elementId: this.elementId,
          meta: {
            renderFeatures: {
              autoHeight: true,
            },
          },
        }),
      );
      this.fullyClosed = true;
      this.fullyOpen = false;
    }
  }

  private updateStyles(): void {
    const style = this.props?.style;

    this.containerBorderRadius = style?.container?.radius ?? this.containerBorderRadius;
    this.headerTitleColor = style?.header?.title
      ? getBorderColor(style?.header.title)
      : this.headerTitleColor;
    this.headerIconColor = style?.header?.icon
      ? getBorderColor(style?.header.icon)
      : this.headerIconColor;
    this.headerFill = style?.header?.fill ? getBackground(style?.header.fill) : this.headerFill;
    this.contentDividerColor = style?.content?.divider
      ? getBorderColor(style?.content.divider)
      : this.contentDividerColor;
    this.contentFill = style?.content?.fill ? getBackground(style?.content.fill) : this.contentFill;

    this.internalPadding = style?.general?.offset.padding ?? DEFAULT_ELEMENT_PADDING;

    this.wrapperStyles = {
      ...makeBorderStyles(style?.container?.outline, style?.container?.outlineWidth),
      ...makeContainerVisualEffectStyles(style?.container?.visualEffect),
    };
  }
}
